

import { useState, useEffect } from "react";
import { Bell, Calendar, User, MessageCircle, Info, CheckCircle, Clock } from "lucide-react";
import { Button } from "@/components/ui/button";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useNavigate } from "react-router-dom";

// Define notification type
interface Notification {
  id: string;
  content: string;
  type: string;
  created_at: string;
  read: boolean;
  link?: string;
}

const Notifications = () => {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [filter, setFilter] = useState("all");
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();
  const { user } = useAuth();
  const navigate = useNavigate();
  
  const unreadCount = notifications.filter(n => !n.read).length;
  
  // Format date using a consistent format
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInDays = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60 * 24));
    
    if (diffInDays === 0) {
      return "Today";
    } else if (diffInDays === 1) {
      return "Yesterday";
    } else if (diffInDays < 7) {
      return `${diffInDays} days ago`;
    } else {
      return date.toLocaleDateString('en-US', { 
        month: 'short', 
        day: 'numeric', 
        year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined 
      });
    }
  };
  
  // Fetch notifications from the database
  useEffect(() => {
    const fetchNotifications = async () => {
      if (!user?.id) return;
      
      setLoading(true);
      try {
        console.log("🔄 FETCH: Starting notifications fetch for user:", user.id);
        
        const { data, error } = await supabase
          .from('notifications')
          .select('*')
          .eq('receiver_id', user.id)
          .order('created_at', { ascending: false });
          
        if (error) {
          console.error("❌ FETCH ERROR:", error);
          throw error;
        }
        
        console.log("✅ FETCH SUCCESS: Found", data?.length || 0, "notifications");
        console.log("📋 FETCH DATA:", data);
        
        setNotifications(data || []);
      } catch (error) {
        console.error("💥 FETCH EXCEPTION:", error);
        toast({
          title: "Error",
          description: "Failed to load notifications",
          variant: "destructive",
        });
      } finally {
        setLoading(false);
      }
    };

    fetchNotifications();

    // Set up real-time subscription for notifications
    console.log("🔄 REALTIME: Setting up subscription for user:", user?.id);
    const channel = supabase
      .channel('notifications-realtime-comprehensive')
      .on('postgres_changes', {
        event: 'INSERT',
        schema: 'public',
        table: 'notifications',
        filter: `receiver_id=eq.${user?.id}`
      }, (payload) => {
        console.log("🔔 === REALTIME NOTIFICATION INSERT ===");
        console.log("📨 REALTIME INSERT payload:", payload);
        const newNotification = payload.new as Notification;
        
        console.log("➕ REALTIME: Adding notification to state:", newNotification);
        
        setNotifications(prev => {
          const updated = [newNotification, ...prev];
          console.log("📊 REALTIME: Updated notification count:", updated.length);
          return updated;
        });
        
        // Show toast for new message notifications
        if (newNotification.type === 'new_message') {
          console.log("🔔 REALTIME: Showing toast for new message");
          toast({
            title: "New Message",
            description: newNotification.content,
          });
        }
        
        console.log("=== END REALTIME NOTIFICATION INSERT ===");
      })
      .on('postgres_changes', {
        event: 'UPDATE',
        schema: 'public',
        table: 'notifications',
        filter: `receiver_id=eq.${user?.id}`
      }, (payload) => {
        console.log("🔄 REALTIME UPDATE:", payload);
        const updatedNotification = payload.new as Notification;
        setNotifications(prev => 
          prev.map(notif => 
            notif.id === updatedNotification.id ? updatedNotification : notif
          )
        );
      })
      .subscribe((status) => {
        console.log("📡 REALTIME STATUS:", status);
      });

    return () => {
      console.log("🧹 CLEANUP: Removing subscription");
      supabase.removeChannel(channel);
    };
  }, [user, toast]);
  
  // Mark all as read
  const markAllAsRead = async () => {
    if (!user?.id || unreadCount === 0) return;
    
    try {
      const { error } = await supabase
        .from('notifications')
        .update({ read: true })
        .eq('receiver_id', user.id)
        .eq('read', false);
        
      if (error) {
        throw error;
      }
      
      setNotifications(notifications.map(n => ({ ...n, read: true })));
      
      toast({
        title: "Success",
        description: "All notifications marked as read"
      });
    } catch (error) {
      console.error("Error marking all as read:", error);
      toast({
        title: "Error",
        description: "Failed to update notifications",
        variant: "destructive",
      });
    }
  };
  
  // Mark single notification as read
  const markAsRead = async (id: string) => {
    try {
      const { error } = await supabase
        .from('notifications')
        .update({ read: true })
        .eq('id', id);
        
      if (error) {
        throw error;
      }
      
      setNotifications(notifications.map(n => 
        n.id === id ? { ...n, read: true } : n
      ));
    } catch (error) {
      console.error("Error marking notification as read:", error);
      toast({
        title: "Error",
        description: "Failed to update notification",
        variant: "destructive",
      });
    }
  };
  
  // Handle notification click
  const handleNotificationClick = (notification: Notification) => {
    console.log("🔗 CLICK: Notification clicked:", notification.id, notification.type);
    
    if (!notification.read) {
      console.log("📖 CLICK: Marking as read");
      markAsRead(notification.id);
    }
    
    if (notification.link) {
      console.log("🔗 CLICK: Navigating to:", notification.link);
      navigate(notification.link);
    } else {
      console.log("⚠️ CLICK: No link provided");
    }
  };
  
  // Show all notifications
  const filteredNotifications = notifications;
  
  // Get icon based on notification type
  const getNotificationIcon = (type: string) => {
    switch (type) {
      case "new_message":
        return <MessageCircle className="text-blue-500" />;
      case "application":
      case "application_approved":
      case "application_rejected":
        return <Calendar className="text-purple-500" />;
      case "system":
        return <Info className="text-green-500" />;
      case "update":
        return <CheckCircle className="text-orange-500" />;
      case "payment_received":
      case "message_enabled":
        return <CheckCircle className="text-green-500" />;
      default:
        return <Bell className="text-gray-500" />;
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-gray-50">
      <Navbar />
      
      {/* Hero Banner */}
      <div className="w-full bg-cover bg-center h-64 relative" style={{ backgroundImage: "url('/lovable-uploads/63560c3c-d7c3-4d91-b852-d2cd79229bf8.png')" }}>
        <div className="absolute inset-0 bg-emu-blue/20"></div>
        <div className="container mx-auto px-4 h-full flex items-center relative z-10">
          <div className="pt-20 pb-10">
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900">
              Notifications
            </h1>
            <p className="text-gray-600 mt-2">
              Stay updated with your mentoring activities
            </p>
          </div>
        </div>
      </div>
      
      <main className="flex-grow pt-8 pb-12">
        <div className="container mx-auto px-4 md:px-6">
          <div className="max-w-3xl mx-auto">
            {/* Filter Bar */}
            <div className="bg-white rounded-lg shadow-sm border p-4 mb-6">
              <div className="flex items-center">
                <Button 
                  variant="default"
                  size="sm"
                  className="bg-emu-blue"
                >
                  All
                </Button>
              </div>
            </div>
            
            {/* Notifications List */}
            <div className="bg-white rounded-lg shadow-sm border divide-y">
              {loading ? (
                <div className="p-8 text-center">
                  <div className="mx-auto h-12 w-12 rounded-full border-4 border-emu-blue border-t-transparent animate-spin"></div>
                  <p className="mt-4 text-gray-500">Loading notifications...</p>
                </div>
              ) : filteredNotifications.length > 0 ? (
                filteredNotifications.map((notification) => (
                  <div 
                    key={notification.id} 
                    className={`p-4 hover:bg-gray-50 transition-colors ${!notification.read ? 'bg-blue-50/30' : ''} ${notification.link ? 'cursor-pointer' : ''}`}
                    onClick={() => notification.link && handleNotificationClick(notification)}
                  >
                    <div className="flex gap-4">
                      <div className="h-10 w-10 rounded-full bg-gray-100 flex items-center justify-center shrink-0">
                        {getNotificationIcon(notification.type)}
                      </div>
                      <div className="flex-1">
                        <p className={`text-gray-800 mb-1 ${!notification.read ? 'font-medium' : ''}`}>
                          {notification.content}
                        </p>
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-1 text-sm text-gray-500">
                            <Clock size={14} />
                            <span>{formatDate(notification.created_at)}</span>
                            <span className="ml-2 px-2 py-1 bg-gray-100 rounded text-xs">
                              {notification.type}
                            </span>
                          </div>
                          {!notification.read && (
                            <Button 
                              variant="ghost" 
                              size="sm" 
                              onClick={(e) => {
                                e.stopPropagation();
                                markAsRead(notification.id);
                              }}
                              className="text-emu-blue text-xs"
                            >
                              Mark as read
                            </Button>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <div className="p-8 text-center">
                  <div className="mx-auto h-12 w-12 rounded-full bg-gray-100 flex items-center justify-center mb-4">
                    <Bell className="text-gray-400" />
                  </div>
                  <h3 className="text-gray-700 font-medium mb-1">No notifications</h3>
                  <p className="text-gray-500 text-sm">
                    You're all caught up!
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </main>
      
      <Footer />
    </div>
  );
};

export default Notifications;

